import { animationFrameScheduler, fromEvent, merge, Observable, Subject, timer } from 'rxjs'
import { exhaustMap, filter, map, skip, take, takeUntil, tap } from 'rxjs/operators'
import { AsyncDirective } from 'lit/async-directive.js'
import { directive, type ElementPart, type PartInfo, PartType } from 'lit/directive.js'
import { SPRING_SNAPPY } from '@schmancy/utils/animation'
import { reducedMotion$ } from './reduced-motion'
import { type DropAxis, hideDropIndicator, hideDropZone, showDropIndicator, showDropZone } from './drop-indicator'

/**
 * `reorder()` — one list, reordered by pointer or by keyboard.
 *
 * Applied to the LIST CONTAINER, not to each row: one delegated session for
 * the whole list, so a list that grows or shrinks costs no re-wiring and a
 * gesture already in flight owns the list until it ends.
 *
 * The markup contract is two data attributes:
 *
 *   - `data-reorder-item="<id>"` on each row, the id matching an entry of the
 *     `ids` array handed to the directive.
 *   - `data-reorder-handle` on the grab affordance inside that row. The
 *     directive gives the handle its button role, its place in the tab order,
 *     its accessible name, and `touch-action: none` — a handle a keyboard or
 *     an automation harness cannot reach is a defect this primitive refuses to
 *     ship rather than one each consumer remembers to prevent.
 *
 * A consumer whose list is divided into containers marks each one
 * `data-reorder-zone="<key>"`. While a drag is in flight the zone under the
 * pointer is outlined whenever it is not the one the row was picked up from,
 * and the `reorder` event carries that zone's key — the insertion line says
 * where in the order, the outline says which container.
 *
 * Pointer sessions are PointerEvent-based (mouse, touch, stylus alike) and
 * close on `pointerup` OR `pointercancel` — touch and stylus fire the latter,
 * not the former, when the OS appropriates the gesture, and a session that
 * only listens for `pointerup` stays open forever after that. `pointerup`
 * commits the move; `pointercancel` discards it, because a gesture the OS took
 * away never expressed an intent to drop.
 *
 * Hit-testing runs against the container's OWN root — `shadowRoot`, not
 * `document` — because `document.elementFromPoint` retargets to the outermost
 * shadow host, so the row under the finger is invisible from outside the root
 * that renders it and every drop silently no-ops. It reads the whole hit-test
 * STACK at that point rather than its top element, so a list under a scroll
 * cue, a sticky head or any other overlay still drops onto the row beneath it.
 */

export interface ReorderLabels {
	/** Accessible name given to a handle that carries none of its own. */
	handle: (name: string) => string
	grabbed: (name: string, position: number, total: number) => string
	moved: (name: string, position: number, total: number) => string
	dropped: (name: string, position: number, total: number) => string
	cancelled: (name: string) => string
}

export interface ReorderOptions {
	/** Lock the list without removing its handles — a save in flight, a role that may read but not edit. */
	disabled?: boolean
	/** Announcement copy. Supply translations here; the defaults are English. */
	labels?: Partial<ReorderLabels>
}

export type SchmancyReorderEvent = CustomEvent<{
	/** The row that moved. */
	id: string
	/**
	 * Its index before the move, and after. Equal indices with a non-null
	 * `zone` mean the row changed container without changing its place in the
	 * order — the zone is the whole of what the drop changed.
	 */
	from: number
	to: number
	/** The whole list in its new order — the value a consumer persists. */
	orderedIds: string[]
	via: 'pointer' | 'keyboard'
	/**
	 * The `data-reorder-zone` the row landed in, when that is not the zone it
	 * started in. Null on a same-zone move, on every list with no zones, and on
	 * the keyboard path — arrow keys move within the order, never between zones.
	 */
	zone: string | null
}>

const DEFAULT_LABELS: ReorderLabels = {
	handle: name => `Reorder ${name}`,
	grabbed: (name, position, total) =>
		`${name} grabbed, position ${position} of ${total}. Use the arrow keys to move it, space to drop, escape to cancel.`,
	moved: (name, position, total) => `${name}, position ${position} of ${total}.`,
	dropped: (name, position, total) => `${name} dropped at position ${position} of ${total}.`,
	cancelled: name => `${name} returned to its original position.`,
}

/**
 * The list with the entry at `from` moved to `to`. Out-of-range or equal
 * indices return the list unchanged, so no caller has to guard the degenerate
 * move itself.
 */
export function reorderedIds(ids: readonly string[], from: number, to: number): string[] {
	const next = [...ids]
	if (from < 0 || to < 0 || from >= next.length || to >= next.length || from === to) return next
	next.splice(to, 0, ...next.splice(from, 1))
	return next
}

interface ReorderRecord {
	ids: readonly string[]
	disabled: boolean
	labels: ReorderLabels
	destroy$: Subject<void>
	/** The row a pointer is currently carrying, the zone it was picked up from, and the axis measured then. */
	dragging: string | null
	zoneAtPickup: string | null
	axis: DropAxis
	/** The row the keyboard has picked up, and the order it was picked up from. */
	grabbed: string | null
	orderAtGrab: readonly string[]
}

const records = new WeakMap<HTMLElement, ReorderRecord>()

let liveRegion: HTMLElement | null = null

function announce(message: string): void {
	if (!liveRegion) {
		liveRegion = document.createElement('div')
		liveRegion.setAttribute('role', 'status')
		liveRegion.setAttribute('aria-live', 'polite')
		Object.assign(liveRegion.style, {
			position: 'fixed',
			width: '1px',
			height: '1px',
			margin: '-1px',
			padding: '0',
			border: '0',
			overflow: 'hidden',
			clipPath: 'inset(50%)',
			whiteSpace: 'nowrap',
		})
		document.body.appendChild(liveRegion)
	}
	// Text identical to what is already there is not re-announced by any screen
	// reader; the trailing space makes a repeated move audible.
	liveRegion.textContent = liveRegion.textContent === message ? `${message} ` : message
}

function rows(container: HTMLElement): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>('[data-reorder-item]'))
}

function rowFor(container: HTMLElement, id: string): HTMLElement | null {
	return container.querySelector<HTMLElement>(`[data-reorder-item="${CSS.escape(id)}"]`)
}

function nameFor(row: HTMLElement | null): string {
	if (!row) return 'item'
	const label = row.getAttribute('aria-label')
	if (label) return label
	const text = row.textContent?.replace(/\s+/g, ' ').trim()
	return text && text.length > 0 ? text.slice(0, 80) : 'item'
}

/**
 * The row under a viewport point, seen from the container's own root.
 *
 * elementsFromPoint, plural: the question is which ROW is under the pointer,
 * and the singular form answers a different one — what is topmost — so any
 * element painted over the list that resolves to no row ends the search at
 * itself and the drop silently finds nothing. A list is under such an element
 * routinely: a scroll cue at its edge, a sticky head, a floating affordance.
 * The full hit-test stack is walked instead and the first entry that resolves
 * to a row of THIS container wins, which is robust against every overlay
 * rather than against the one that was noticed.
 */
function rowAt(container: HTMLElement, x: number, y: number): HTMLElement | null {
	const root = container.getRootNode()
	const hitRoot = root instanceof ShadowRoot ? root : document
	for (const element of hitRoot.elementsFromPoint(x, y)) {
		const hit = element.closest<HTMLElement>('[data-reorder-item]')
		if (hit && container.contains(hit)) return hit
	}
	return null
}

/**
 * The zone a row is filed under: its nearest `[data-reorder-zone]` ancestor
 * inside the list. A list whose consumer marked no zones has none, and every
 * move in it is a plain reorder.
 */
function zoneOf(container: HTMLElement, row: HTMLElement | null): HTMLElement | null {
	const zone = row?.closest<HTMLElement>('[data-reorder-zone]') ?? null
	return zone && container.contains(zone) ? zone : null
}

function zoneKey(zone: HTMLElement | null): string | null {
	return zone?.dataset.reorderZone ?? null
}

/** Where a point falls relative to a row's midpoint, on the axis the order runs along. */
function sideOf(row: HTMLElement, x: number, y: number, axis: DropAxis): 'before' | 'after' {
	const rect = row.getBoundingClientRect()
	const past = axis === 'across' ? x > rect.left + rect.width / 2 : y > rect.top + rect.height / 2
	return past ? 'after' : 'before'
}

function measure(container: HTMLElement): Map<string, DOMRect> {
	return new Map(rows(container).map(row => [row.dataset.reorderItem ?? '', row.getBoundingClientRect()]))
}

/**
 * FLIP the settle: rows measured before the consumer re-rendered are animated
 * from where they were to where they now are, so the list closes its gap
 * instead of teleporting.
 */
function settle(container: HTMLElement, before: Map<string, DOMRect>, destroy$: Subject<void>): void {
	if (reducedMotion$.value) return
	timer(0, 0, animationFrameScheduler)
		.pipe(skip(1), take(1), takeUntil(destroy$))
		.subscribe(() => {
			for (const [id, old] of before) {
				const row = rowFor(container, id)
				if (!row) continue
				const now = row.getBoundingClientRect()
				const dx = old.left - now.left
				const dy = old.top - now.top
				if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue
				row.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
					duration: SPRING_SNAPPY.duration,
					easing: SPRING_SNAPPY.easingFallback,
				})
			}
		})
}

function setLifted(row: HTMLElement | null, lifted: boolean): void {
	if (!row) return
	if (lifted) {
		row.setAttribute('data-reorder-dragging', '')
		row.style.opacity = '0.5'
		if (!reducedMotion$.value) row.style.transition = 'opacity 120ms ease'
		return
	}
	row.removeAttribute('data-reorder-dragging')
	row.style.removeProperty('opacity')
	row.style.removeProperty('transition')
}

/** The id of the row whose handle an event landed on — '' for everything else. */
function handledId(target: EventTarget | null): string {
	if (!(target instanceof Element)) return ''
	const handle = target.closest<HTMLElement>('[data-reorder-handle]')
	return handle?.closest<HTMLElement>('[data-reorder-item]')?.dataset.reorderItem ?? ''
}

/**
 * `AsyncDirective`, not `Directive`: lit-html notifies a directive of its part's
 * connection only through `_$notifyDirectiveConnectionChanged`, which exists on
 * `AsyncDirective` alone — so `disconnected()`/`reconnected()` declared on a
 * plain `Directive` subclass are never called at all. A list removed mid-drag
 * would leave a live `pointermove` subscription on `window`, an insertion line
 * and a zone outline stranded in `document.body`, and `document.body`'s cursor
 * stuck at `grabbing` for the page's life.
 */
class ReorderDirective extends AsyncDirective {
	private container?: HTMLElement

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) throw new Error('reorder directive can only be used on elements')
	}

	render(ids: readonly string[], options?: ReorderOptions) {
		void ids
		void options
		return undefined
	}

	update(part: ElementPart, [ids, options = {}]: [readonly string[], ReorderOptions?]) {
		const container = part.element as HTMLElement
		this.container = container
		const existing = records.get(container)

		if (existing) {
			existing.ids = ids
			existing.disabled = options.disabled ?? false
			existing.labels = { ...DEFAULT_LABELS, ...options.labels }
			this.dressHandles(container, existing)
			return undefined
		}

		const record: ReorderRecord = {
			ids,
			disabled: options.disabled ?? false,
			labels: { ...DEFAULT_LABELS, ...options.labels },
			destroy$: new Subject<void>(),
			dragging: null,
			zoneAtPickup: null,
			axis: 'down',
			grabbed: null,
			orderAtGrab: ids,
		}
		records.set(container, record)
		this.dressHandles(container, record)
		// An update can arrive while the part is disconnected — lit's own note on
		// `AsyncDirective` — and subscribing then would hold a detached list's
		// listeners with nothing to tear them down. `reconnected()` picks it up.
		if (this.isConnected) this.listen(container, record)
		return undefined
	}

	/**
	 * Every handle's affordances, reapplied on each render because the rows are
	 * the consumer's and may be recreated at will. A handle arrives as whatever
	 * the consumer chose to draw — an icon, a glyph, a bare div — and leaves
	 * here focusable, named, and role-carrying.
	 */
	private dressHandles(container: HTMLElement, record: ReorderRecord): void {
		const locked = record.disabled || record.ids.length < 2
		for (const row of rows(container)) {
			const handle = row.querySelector<HTMLElement>('[data-reorder-handle]')
			if (!handle) continue
			if (!handle.hasAttribute('role')) handle.setAttribute('role', 'button')
			if (!handle.hasAttribute('tabindex')) handle.tabIndex = 0
			handle.setAttribute('aria-disabled', String(locked))
			if (!handle.hasAttribute('aria-label') && !handle.hasAttribute('aria-labelledby')) {
				handle.setAttribute('aria-label', record.labels.handle(nameFor(row)))
			}
			// Without this the browser claims the touch as a scroll and the gesture
			// arrives as a pointercancel before it has travelled a pixel.
			handle.style.touchAction = 'none'
			handle.style.cursor = locked ? '' : 'grab'
			// A list that cannot be reordered says so in the handle, not only in
			// the ARIA — a grip that looks live and answers nothing reads as broken.
			handle.style.opacity = locked ? '0.38' : ''
			handle.style.userSelect = 'none'
		}
	}

	private listen(container: HTMLElement, record: ReorderRecord): void {
		// A directive on an element part runs BEFORE that element's children are
		// committed, so the first update sees an empty list and no handle at all
		// gets its role, its tab stop or its name. Watching the container is what
		// makes the affordance contract hold whenever the rows actually appear —
		// on the first render, and on every row the consumer adds afterwards.
		const rowsChanged$ = new Observable<void>(subscriber => {
			const observer = new MutationObserver(() => subscriber.next())
			observer.observe(container, { childList: true, subtree: true })
			return () => observer.disconnect()
		}).pipe(tap(() => this.dressHandles(container, record)))

		const pointer$ = fromEvent<PointerEvent>(container, 'pointerdown').pipe(
			filter(() => !record.disabled && record.ids.length > 1),
			map(event => ({ event, id: handledId(event.target) })),
			filter(start => start.id !== '' && record.ids.includes(start.id)),
			tap(start => {
				// Suppresses the text selection, the native image drag, and the click
				// the card underneath would otherwise take as an open.
				start.event.preventDefault()
				record.dragging = start.id
				record.zoneAtPickup = zoneKey(zoneOf(container, rowFor(container, start.id)))
				// Measured once per session from how the first two rows actually sit: a
				// list's axis cannot change mid-gesture, and re-reading it per pointermove
				// costs a layout on every frame. A wrapping grid measures `across`, because
				// its first two tiles ARE side by side and its order runs that way through
				// every row it wraps into.
				const [first, second] = rows(container).map(row => row.getBoundingClientRect())
				record.axis =
					first !== undefined &&
					second !== undefined &&
					Math.abs(second.left - first.left) > Math.abs(second.top - first.top)
						? 'across'
						: 'down'
				setLifted(rowFor(container, start.id), true)
				document.body.style.cursor = 'grabbing'
			}),
			// First-wins: a second finger landing on another handle cannot restart
			// the session half-way through a drop.
			exhaustMap(start =>
				fromEvent<PointerEvent>(window, 'pointermove').pipe(
					tap(move => {
						const over = rowAt(container, move.clientX, move.clientY)
						const id = over?.dataset.reorderItem ?? ''
						// The two affordances answer different questions and paint
						// independently: the outline says which zone will receive the row,
						// the line says where inside it.
						const zone = zoneOf(container, over)
						if (zone && zoneKey(zone) !== record.zoneAtPickup) showDropZone(zone)
						else hideDropZone()
						if (!over || id === '' || id === record.dragging) return hideDropIndicator()
						showDropIndicator(over, sideOf(over, move.clientX, move.clientY, record.axis), record.axis)
					}),
					takeUntil(
						merge(fromEvent<PointerEvent>(window, 'pointerup'), fromEvent<PointerEvent>(window, 'pointercancel')).pipe(
							take(1),
							tap(end => this.endPointer(container, record, start.id, end)),
						),
					),
				),
			),
		)

		const keyboard$ = fromEvent<KeyboardEvent>(container, 'keydown').pipe(
			map(event => ({ event, id: handledId(event.target) })),
			filter(stroke => stroke.id !== '' && record.ids.includes(stroke.id)),
			tap(stroke => this.key(container, record, stroke.id, stroke.event)),
		)

		merge(rowsChanged$, pointer$, keyboard$).pipe(takeUntil(record.destroy$)).subscribe()
	}

	private endPointer(container: HTMLElement, record: ReorderRecord, id: string, end: PointerEvent): void {
		const before = measure(container)
		setLifted(rowFor(container, id), false)
		document.body.style.removeProperty('cursor')
		hideDropIndicator()
		hideDropZone()
		const pickedUpIn = record.zoneAtPickup
		record.dragging = null
		record.zoneAtPickup = null

		// A gesture the OS took away — a system swipe, an incoming call — never
		// expressed an intent to drop: it ends the session and changes nothing.
		if (end.type === 'pointercancel') return

		const over = rowAt(container, end.clientX, end.clientY)
		const overId = over?.dataset.reorderItem ?? ''
		if (!over || overId === '' || overId === id) return

		const from = record.ids.indexOf(id)
		const overIndex = record.ids.indexOf(overId)
		if (from === -1 || overIndex === -1) return

		const insertion = overIndex + (sideOf(over, end.clientX, end.clientY, record.axis) === 'after' ? 1 : 0)
		// The insertion index counts the dragged row where it still sits, so any
		// landing spot past it loses one place once the row is lifted out.
		const to = insertion > from ? insertion - 1 : insertion
		const landedIn = zoneKey(zoneOf(container, over))
		const zone = landedIn === pickedUpIn ? null : landedIn
		// A zone change is a reason to commit on its own: a row carried into
		// another container whose flat index happens not to move still changed
		// the one fact the consumer drags it for.
		if (to === from && zone === null) return

		this.commit(container, record, id, from, to, 'pointer', zone)
		settle(container, before, record.destroy$)
	}

	/**
	 * The pointerless path, and the only one a screen-reader user or an
	 * automation harness has: space grabs, the arrows move, space drops, escape
	 * puts it back.
	 */
	private key(container: HTMLElement, record: ReorderRecord, id: string, event: KeyboardEvent): void {
		if (record.disabled || record.ids.length < 2) return
		const name = nameFor(rowFor(container, id))
		const total = record.ids.length
		const at = record.ids.indexOf(id)

		if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
			event.preventDefault()
			if (record.grabbed === id) {
				record.grabbed = null
				setLifted(rowFor(container, id), false)
				announce(record.labels.dropped(name, at + 1, total))
				return
			}
			record.grabbed = id
			record.orderAtGrab = record.ids
			setLifted(rowFor(container, id), true)
			announce(record.labels.grabbed(name, at + 1, total))
			return
		}

		if (record.grabbed !== id) return

		if (event.key === 'Escape') {
			event.preventDefault()
			const home = record.orderAtGrab.indexOf(id)
			record.grabbed = null
			setLifted(rowFor(container, id), false)
			announce(record.labels.cancelled(name))
			if (home !== -1 && home !== at) this.move(container, record, id, at, home)
			return
		}

		const step =
			event.key === 'ArrowUp' || event.key === 'ArrowLeft'
				? -1
				: event.key === 'ArrowDown' || event.key === 'ArrowRight'
					? 1
					: event.key === 'Home'
						? -at
						: event.key === 'End'
							? total - 1 - at
							: 0
		if (step === 0) return

		event.preventDefault()
		const to = Math.min(total - 1, Math.max(0, at + step))
		if (to === at) return
		this.move(container, record, id, at, to)
		announce(record.labels.moved(name, to + 1, total))
	}

	/** A keyboard move: commit it, settle the list, and keep the handle focused across the consumer's re-render. */
	private move(container: HTMLElement, record: ReorderRecord, id: string, from: number, to: number): void {
		const before = measure(container)
		this.commit(container, record, id, from, to, 'keyboard', null)
		settle(container, before, record.destroy$)
		// A repeat() that rebuilds its rows rather than moving them would drop
		// focus mid-reorder, stranding a keyboard user outside the list.
		timer(0, 0, animationFrameScheduler)
			.pipe(skip(1), take(1), takeUntil(record.destroy$))
			.subscribe(() => {
				rowFor(container, id)?.querySelector<HTMLElement>('[data-reorder-handle]')?.focus()
				if (record.grabbed === id) setLifted(rowFor(container, id), true)
			})
	}

	private commit(
		container: HTMLElement,
		record: ReorderRecord,
		id: string,
		from: number,
		to: number,
		via: 'pointer' | 'keyboard',
		zone: string | null,
	): void {
		container.dispatchEvent(
			new CustomEvent('reorder', {
				// Not composed: the listener is the consumer's own template binding on
				// this very container, and a composed event would additionally surface
				// inside the app as if it were the component's public API.
				bubbles: true,
				detail: { id, from, to, orderedIds: reorderedIds(record.ids, from, to), via, zone },
			}),
		)
	}

	override disconnected() {
		const container = this.container
		const record = container ? records.get(container) : undefined
		if (!record) return
		record.destroy$.next()
		record.destroy$.complete()
		record.dragging = null
		record.zoneAtPickup = null
		record.grabbed = null
		hideDropIndicator()
		hideDropZone()
		document.body.style.removeProperty('cursor')
	}

	override reconnected() {
		const container = this.container
		const record = container ? records.get(container) : undefined
		if (!container || !record) return
		record.destroy$ = new Subject<void>()
		// Re-listened here rather than on the next update, because a part
		// reconnecting does not itself re-render: a list waiting for an update that
		// never comes is a list that answers no gesture.
		this.listen(container, record)
	}
}

/**
 * Reorder a list by dragging its handles or by keyboard alone.
 *
 * ```ts
 * html`
 *   <div ${reorder(this.rows.map(r => r.id))} @reorder=${this.persist}>
 *     ${repeat(this.rows, r => r.id, r => html`
 *       <div data-reorder-item=${r.id} aria-label=${r.name}>
 *         <schmancy-icon data-reorder-handle>drag_indicator</schmancy-icon>
 *         ${r.name}
 *       </div>
 *     `)}
 *   </div>
 * `
 * ```
 */
export const reorder = directive(ReorderDirective)
